import { create } from "zustand";
import { logEditorCorrelation } from "../utils/editor";
import type { ViewRData, VSData } from "../types/unity";

export type ViewRDataAuthorityMode = "shadow" | "react";

export type ViewRDataMutationOp =
  | {
      type: "replaceViewRData";
      viewRData: ViewRData;
    }
  | {
      type: "addSpace";
      index: number;
      name: string;
    }
  | {
      type: "duplicateSpace";
      sourceIndex: number;
      targetIndex: number;
      name: string;
    }
  | {
      type: "deleteSpace";
      index: number;
    }
  | {
      type: "renameSpace";
      index: number;
      name: string;
    }
  | {
      type: "selectSpace";
      index: number;
    }
  | {
      type: "reorderSpace";
      fromIndex: number;
      toIndex: number;
    }
  | {
      type: "selectObject";
      spaceIndex: number;
      vseIndex: number;
    }
  | {
      type: "addObject";
      spaceIndex: number;
      vseIndex: number;
      vse: VSData["vseList"][number];
    }
  | {
      type: "renameObject";
      spaceIndex: number;
      vseIndex: number;
      name: string;
    }
  | {
      type: "deleteObject";
      spaceIndex: number;
      vseIndex: number;
    };

export interface ViewRDataDelta {
  mutationId: string;
  baseRevision: number;
  nextRevision: number;
  ops: ViewRDataMutationOp[];
  createdAt: number;
  source: string;
  status: "pending" | "acked" | "failed";
  error?: string;
}

export interface ViewRDataLegacyEventDetail {
  viewRData?: ViewRData;
  spacesCount?: number;
  currentSpaceIndex?: number;
  sourceReason?: string;
  transactionId?: string | null;
  targetSpaceIndex?: number | null;
}

export interface ViewRDataSnapshot {
  viewRData: ViewRData | null;
  revision: number;
  hash: string;
  authority: ViewRDataAuthorityMode;
  currentSpaceIndex: number;
  spacesCount: number;
}

interface MutationContext {
  source?: string;
  mutationId?: string;
  trackDelta?: boolean;
}

interface IngestLegacyContext {
  source?: string;
  sourceReason?: string;
  transactionId?: string | null;
}

interface ViewRDataStoreState {
  viewRData: ViewRData | null;
  revision: number;
  authority: ViewRDataAuthorityMode;
  currentSpaceIndex: number;
  spacesCount: number;
  lastHash: string;
  lastUnityHash: string;
  lastSource: string | null;
  lastSourceReason: string | null;
  lastTransactionId: string | null;
  pendingDeltas: ViewRDataDelta[];
  ingestLegacyEvent: (
    detail: ViewRData | ViewRDataLegacyEventDetail | null | undefined,
    context?: IngestLegacyContext,
  ) => void;
  replaceFromReact: (
    viewRData: ViewRData,
    context?: MutationContext,
  ) => ViewRDataDelta | null;
  addSpace: (
    params?: { name?: string; index?: number },
    context?: MutationContext,
  ) => ViewRDataDelta | null;
  duplicateSpace: (
    sourceIndex: number,
    targetIndex: number,
    name: string,
    context?: MutationContext,
  ) => ViewRDataDelta | null;
  deleteSpace: (
    index: number,
    context?: MutationContext,
  ) => ViewRDataDelta | null;
  renameSpace: (
    index: number,
    name: string,
    context?: MutationContext,
  ) => ViewRDataDelta | null;
  selectSpace: (
    index: number,
    context?: MutationContext,
  ) => ViewRDataDelta | null;
  reorderSpace: (
    fromIndex: number,
    toIndex: number,
    context?: MutationContext,
  ) => ViewRDataDelta | null;
  selectObject: (
    spaceIndex: number,
    vseIndex: number,
    context?: MutationContext,
  ) => ViewRDataDelta | null;
  addObject: (
    spaceIndex: number,
    vse: VSData["vseList"][number],
    context?: MutationContext,
  ) => ViewRDataDelta | null;
  renameObject: (
    spaceIndex: number,
    vseIndex: number,
    name: string,
    context?: MutationContext,
  ) => ViewRDataDelta | null;
  deleteObject: (
    spaceIndex: number,
    vseIndex: number,
    context?: MutationContext,
  ) => ViewRDataDelta | null;
  ackDelta: (mutationId: string) => void;
  failDelta: (mutationId: string, error: string) => void;
  retryDelta: (mutationId: string) => void;
  restorePendingDeltas: (deltas: ViewRDataDelta[]) => number;
  getSnapshot: () => ViewRDataSnapshot;
  reset: () => void;
}

const isDev = process.env.NODE_ENV === "development";

const EMPTY_HASH = "null";

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  }

  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
    .join(",")}}`;
}

export function computeViewRDataHash(viewRData: ViewRData | null): string {
  return viewRData ? stableStringify(viewRData) : EMPTY_HASH;
}

function clampIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  if (!Number.isFinite(index)) return 0;
  return Math.min(Math.max(Math.trunc(index), 0), length - 1);
}

function normalizeViewRData(viewRData: ViewRData): ViewRData {
  const spaces = Array.isArray(viewRData.spaces) ? viewRData.spaces : [];
  return {
    ...viewRData,
    spaces,
    currentSelectVSIndex: clampIndex(
      viewRData.currentSelectVSIndex ?? 0,
      spaces.length,
    ),
  };
}

function createEmptySpace(name: string): VSData {
  return {
    name,
    currentSelectVSEIndex: -1,
    vseList: [],
  };
}

function deepCloneSpaceWithoutComments(source: VSData, name: string): VSData {
  const clone = JSON.parse(JSON.stringify(source)) as VSData;
  return {
    ...clone,
    name,
    // Comment ID와 image bundle key 정책이 확정될 때까지 Comment는 복제하지 않는다.
    commentList: [],
  };
}

function remapIndexAfterReorder(
  currentIndex: number,
  fromIndex: number,
  toIndex: number,
): number {
  if (currentIndex === fromIndex) return toIndex;
  if (
    fromIndex < toIndex &&
    currentIndex > fromIndex &&
    currentIndex <= toIndex
  ) {
    return currentIndex - 1;
  }
  if (
    toIndex < fromIndex &&
    currentIndex >= toIndex &&
    currentIndex < fromIndex
  ) {
    return currentIndex + 1;
  }
  return currentIndex;
}

function resolveNextSpaceName(viewRData: ViewRData): string {
  const existing = new Set(
    viewRData.spaces.map((space) => (space.name ?? "").trim()),
  );
  let index = viewRData.spaces.length + 1;
  while (existing.has(`Space${index}`)) {
    index += 1;
  }
  return `Space${index}`;
}

function extractLegacyViewRData(
  detail: ViewRData | ViewRDataLegacyEventDetail | null | undefined,
): ViewRData | null {
  if (!detail) return null;
  if ("viewRData" in detail && detail.viewRData) {
    return detail.viewRData;
  }
  if ("spaces" in detail && Array.isArray(detail.spaces)) {
    return detail as ViewRData;
  }
  return null;
}

function buildMutationId(type: string): string {
  return `${type}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
}

function commitReactMutation(
  state: ViewRDataStoreState,
  nextViewRData: ViewRData,
  ops: ViewRDataMutationOp[],
  context: MutationContext | undefined,
): {
  state: Partial<ViewRDataStoreState>;
  delta: ViewRDataDelta;
} {
  const normalized = normalizeViewRData(nextViewRData);
  const baseRevision = state.revision;
  const nextRevision = baseRevision + 1;
  const nextHash = computeViewRDataHash(normalized);
  const delta: ViewRDataDelta = {
    mutationId:
      context?.mutationId ?? buildMutationId(ops[0]?.type ?? "mutation"),
    baseRevision,
    nextRevision,
    ops,
    createdAt: Date.now(),
    source: context?.source ?? "react_store",
    status: "pending",
  };

  return {
    delta,
    state: {
      viewRData: normalized,
      revision: nextRevision,
      currentSpaceIndex: normalized.currentSelectVSIndex,
      spacesCount: normalized.spaces.length,
      lastHash: nextHash,
      lastSource: delta.source,
      lastSourceReason: ops[0]?.type ?? null,
      lastTransactionId: null,
      pendingDeltas:
        context?.trackDelta === false
          ? state.pendingDeltas
          : [...state.pendingDeltas, delta],
    },
  };
}

const initialState = {
  viewRData: null,
  revision: 0,
  authority: "shadow" as ViewRDataAuthorityMode,
  currentSpaceIndex: 0,
  spacesCount: 0,
  lastHash: EMPTY_HASH,
  lastUnityHash: EMPTY_HASH,
  lastSource: null,
  lastSourceReason: null,
  lastTransactionId: null,
  pendingDeltas: [] as ViewRDataDelta[],
};

export const useViewRDataStore = create<ViewRDataStoreState>()((set, get) => ({
  ...initialState,

  ingestLegacyEvent: (detail, context) => {
    const incoming = extractLegacyViewRData(detail);
    if (!incoming) return;

    const normalized = normalizeViewRData(incoming);
    const incomingHash = computeViewRDataHash(normalized);
    const current = get();
    const pendingDeltasBefore = current.pendingDeltas.length;
    const nextRevision = current.revision + 1;

    set({
      viewRData: normalized,
      revision: nextRevision,
      currentSpaceIndex:
        typeof (detail as ViewRDataLegacyEventDetail)?.currentSpaceIndex ===
        "number"
          ? clampIndex(
              (detail as ViewRDataLegacyEventDetail).currentSpaceIndex ?? 0,
              normalized.spaces.length,
            )
          : normalized.currentSelectVSIndex,
      spacesCount:
        typeof (detail as ViewRDataLegacyEventDetail)?.spacesCount === "number"
          ? Math.max(
              0,
              Math.trunc(
                (detail as ViewRDataLegacyEventDetail).spacesCount ?? 0,
              ),
            )
          : normalized.spaces.length,
      lastHash: incomingHash,
      lastUnityHash: incomingHash,
      lastSource: context?.source ?? "legacy_sidebar_event",
      lastSourceReason:
        context?.sourceReason ??
        (detail as ViewRDataLegacyEventDetail)?.sourceReason ??
        null,
      lastTransactionId:
        context?.transactionId ??
        (detail as ViewRDataLegacyEventDetail)?.transactionId ??
        null,
    });

    const after = get();
    const shouldDiag =
      context?.source === "react_first_initial_load" ||
      pendingDeltasBefore > 0 ||
      after.pendingDeltas.length > 0;
    if (shouldDiag) {
      logEditorCorrelation("viewr_data_ingest_legacy_diag", undefined, {
        source: context?.source ?? null,
        sourceReason: context?.sourceReason ?? null,
        transactionId: context?.transactionId ?? null,
        revision: after.revision,
        pendingDeltasBefore,
        pendingDeltasAfter: after.pendingDeltas.length,
        spacesCount: after.spacesCount,
        vseCountSpace0: after.viewRData?.spaces?.[0]?.vseList?.length ?? 0,
      });
    }
  },

  replaceFromReact: (viewRData, context) => {
    let committed: ViewRDataDelta | null = null;
    set((state) => {
      const next = commitReactMutation(
        state,
        viewRData,
        [
          {
            type: "replaceViewRData",
            viewRData: normalizeViewRData(viewRData),
          },
        ],
        context,
      );
      committed = next.delta;
      return next.state;
    });
    return committed;
  },

  addSpace: (params, context) => {
    let committed: ViewRDataDelta | null = null;
    set((state) => {
      if (!state.viewRData) return {};
      const insertIndex = clampIndex(
        params?.index ?? state.viewRData.spaces.length,
        state.viewRData.spaces.length + 1,
      );
      const name =
        params?.name?.trim() || resolveNextSpaceName(state.viewRData);
      const spaces = [...state.viewRData.spaces];
      spaces.splice(insertIndex, 0, createEmptySpace(name));
      const nextViewRData = {
        ...state.viewRData,
        spaces,
        currentSelectVSIndex: insertIndex,
      };
      const next = commitReactMutation(
        state,
        nextViewRData,
        [{ type: "addSpace", index: insertIndex, name }],
        context,
      );
      committed = next.delta;
      return next.state;
    });
    return committed;
  },

  duplicateSpace: (sourceIndex, targetIndex, name, context) => {
    const trimmedName = name.trim();
    if (!trimmedName) return null;
    let committed: ViewRDataDelta | null = null;
    set((state) => {
      if (!state.viewRData?.spaces.length) return {};
      if (sourceIndex < 0 || sourceIndex >= state.viewRData.spaces.length)
        return {};
      const insertIndex = Math.min(
        Math.max(Math.trunc(targetIndex), 0),
        state.viewRData.spaces.length,
      );
      const sourceSpace = state.viewRData.spaces[sourceIndex];
      const spaces = [...state.viewRData.spaces];
      spaces.splice(
        insertIndex,
        0,
        deepCloneSpaceWithoutComments(sourceSpace, trimmedName),
      );
      const next = commitReactMutation(
        state,
        { ...state.viewRData, spaces, currentSelectVSIndex: insertIndex },
        [
          {
            type: "duplicateSpace",
            sourceIndex,
            targetIndex: insertIndex,
            name: trimmedName,
          },
        ],
        context,
      );
      committed = next.delta;
      return next.state;
    });
    return committed;
  },

  deleteSpace: (index, context) => {
    let committed: ViewRDataDelta | null = null;
    set((state) => {
      if (!state.viewRData?.spaces.length) return {};
      const deleteIndex = clampIndex(index, state.viewRData.spaces.length);
      const spaces = state.viewRData.spaces.filter((_, i) => i !== deleteIndex);
      const nextViewRData = {
        ...state.viewRData,
        spaces,
        currentSelectVSIndex: clampIndex(
          Math.min(deleteIndex, spaces.length - 1),
          spaces.length,
        ),
      };
      const next = commitReactMutation(
        state,
        nextViewRData,
        [{ type: "deleteSpace", index: deleteIndex }],
        context,
      );
      committed = next.delta;
      return next.state;
    });
    return committed;
  },

  renameSpace: (index, name, context) => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    let committed: ViewRDataDelta | null = null;
    set((state) => {
      if (!state.viewRData?.spaces.length) return {};
      const renameIndex = clampIndex(index, state.viewRData.spaces.length);
      const duplicate = state.viewRData.spaces.some(
        (space, i) =>
          i !== renameIndex && (space.name ?? "").trim() === trimmed,
      );
      if (duplicate) return {};
      const spaces = state.viewRData.spaces.map((space, i) =>
        i === renameIndex ? { ...space, name: trimmed } : space,
      );
      const next = commitReactMutation(
        state,
        { ...state.viewRData, spaces },
        [{ type: "renameSpace", index: renameIndex, name: trimmed }],
        context,
      );
      committed = next.delta;
      return next.state;
    });
    return committed;
  },

  selectSpace: (index, context) => {
    let committed: ViewRDataDelta | null = null;
    set((state) => {
      if (!state.viewRData?.spaces.length) return {};
      const selectedIndex = clampIndex(index, state.viewRData.spaces.length);
      if (state.viewRData.currentSelectVSIndex === selectedIndex) return {};
      const next = commitReactMutation(
        state,
        { ...state.viewRData, currentSelectVSIndex: selectedIndex },
        [{ type: "selectSpace", index: selectedIndex }],
        context,
      );
      committed = next.delta;
      return next.state;
    });
    return committed;
  },

  reorderSpace: (fromIndex, toIndex, context) => {
    let committed: ViewRDataDelta | null = null;
    set((state) => {
      const spaces = state.viewRData?.spaces;
      if (!spaces?.length) return {};
      if (
        fromIndex < 0 ||
        fromIndex >= spaces.length ||
        toIndex < 0 ||
        toIndex >= spaces.length ||
        fromIndex === toIndex
      ) {
        return {};
      }
      const reorderedSpaces = [...spaces];
      const [movingSpace] = reorderedSpaces.splice(fromIndex, 1);
      reorderedSpaces.splice(toIndex, 0, movingSpace);
      const nextCurrentIndex = remapIndexAfterReorder(
        state.viewRData.currentSelectVSIndex,
        fromIndex,
        toIndex,
      );
      const next = commitReactMutation(
        state,
        {
          ...state.viewRData,
          spaces: reorderedSpaces,
          currentSelectVSIndex: nextCurrentIndex,
        },
        [{ type: "reorderSpace", fromIndex, toIndex }],
        context,
      );
      committed = next.delta;
      return next.state;
    });
    return committed;
  },

  selectObject: (spaceIndex, vseIndex, context) => {
    let committed: ViewRDataDelta | null = null;
    set((state) => {
      if (!state.viewRData?.spaces.length) return {};
      const safeSpaceIndex = clampIndex(
        spaceIndex,
        state.viewRData.spaces.length,
      );
      const space = state.viewRData.spaces[safeSpaceIndex];
      if (
        !space?.vseList?.length ||
        vseIndex < 0 ||
        vseIndex >= space.vseList.length
      ) {
        return {};
      }
      if (space.currentSelectVSEIndex === vseIndex) return {};
      const spaces = state.viewRData.spaces.map((candidate, i) =>
        i === safeSpaceIndex
          ? { ...candidate, currentSelectVSEIndex: vseIndex }
          : candidate,
      );
      const next = commitReactMutation(
        state,
        { ...state.viewRData, spaces },
        [{ type: "selectObject", spaceIndex: safeSpaceIndex, vseIndex }],
        context,
      );
      committed = next.delta;
      return next.state;
    });
    return committed;
  },

  addObject: (spaceIndex, vse, context) => {
    let committed: ViewRDataDelta | null = null;
    set((state) => {
      if (!state.viewRData?.spaces.length) return {};
      const safeSpaceIndex = clampIndex(
        spaceIndex,
        state.viewRData.spaces.length,
      );
      const space = state.viewRData.spaces[safeSpaceIndex];
      const vseIndex = space.vseList?.length ?? 0;
      const spaces = state.viewRData.spaces.map((candidate, i) =>
        i === safeSpaceIndex
          ? {
              ...candidate,
              currentSelectVSEIndex: vseIndex,
              vseList: [...(candidate.vseList ?? []), vse],
            }
          : candidate,
      );
      const next = commitReactMutation(
        state,
        { ...state.viewRData, spaces },
        [{ type: "addObject", spaceIndex: safeSpaceIndex, vseIndex, vse }],
        context,
      );
      committed = next.delta;
      return next.state;
    });
    return committed;
  },

  renameObject: (spaceIndex, vseIndex, name, context) => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    let committed: ViewRDataDelta | null = null;
    set((state) => {
      if (!state.viewRData?.spaces.length) return {};
      const safeSpaceIndex = clampIndex(
        spaceIndex,
        state.viewRData.spaces.length,
      );
      const space = state.viewRData.spaces[safeSpaceIndex];
      if (
        !space?.vseList?.length ||
        vseIndex < 0 ||
        vseIndex >= space.vseList.length
      ) {
        return {};
      }
      const spaces = state.viewRData.spaces.map((candidate, i) =>
        i === safeSpaceIndex
          ? {
              ...candidate,
              vseList: candidate.vseList.map((vse, j) =>
                j === vseIndex ? { ...vse, name: trimmed } : vse,
              ),
            }
          : candidate,
      );
      const next = commitReactMutation(
        state,
        { ...state.viewRData, spaces },
        [
          {
            type: "renameObject",
            spaceIndex: safeSpaceIndex,
            vseIndex,
            name: trimmed,
          },
        ],
        context,
      );
      committed = next.delta;
      return next.state;
    });
    return committed;
  },

  deleteObject: (spaceIndex, vseIndex, context) => {
    let committed: ViewRDataDelta | null = null;
    set((state) => {
      if (!state.viewRData?.spaces.length) return {};
      const safeSpaceIndex = clampIndex(
        spaceIndex,
        state.viewRData.spaces.length,
      );
      const space = state.viewRData.spaces[safeSpaceIndex];
      if (
        !space?.vseList?.length ||
        vseIndex < 0 ||
        vseIndex >= space.vseList.length
      ) {
        return {};
      }
      const spaces = state.viewRData.spaces.map((candidate, i) => {
        if (i !== safeSpaceIndex) return candidate;
        const vseList = candidate.vseList.filter((_, j) => j !== vseIndex);
        return {
          ...candidate,
          vseList,
          currentSelectVSEIndex: clampIndex(
            Math.min(vseIndex, vseList.length - 1),
            vseList.length,
          ),
        };
      });
      const next = commitReactMutation(
        state,
        { ...state.viewRData, spaces },
        [{ type: "deleteObject", spaceIndex: safeSpaceIndex, vseIndex }],
        context,
      );
      committed = next.delta;
      return next.state;
    });
    return committed;
  },

  ackDelta: (mutationId) => {
    set((state) => ({
      pendingDeltas: state.pendingDeltas.filter(
        (delta) => delta.mutationId !== mutationId,
      ),
    }));
  },

  failDelta: (mutationId, error) => {
    set((state) => ({
      pendingDeltas: state.pendingDeltas.map((delta) =>
        delta.mutationId === mutationId
          ? { ...delta, status: "failed", error }
          : delta,
      ),
    }));
  },

  retryDelta: (mutationId) => {
    set((state) => ({
      pendingDeltas: state.pendingDeltas.map((delta) =>
        delta.mutationId === mutationId
          ? { ...delta, status: "pending", error: undefined }
          : delta,
      ),
    }));
  },

  restorePendingDeltas: (deltas) => {
    if (!Array.isArray(deltas) || deltas.length === 0) {
      return 0;
    }

    let restoredCount = 0;
    set((state) => {
      const pendingById = new Map(
        state.pendingDeltas.map((delta) => [delta.mutationId, delta]),
      );

      for (const delta of deltas) {
        if (!delta?.mutationId || pendingById.has(delta.mutationId)) {
          continue;
        }
        restoredCount += 1;
        pendingById.set(delta.mutationId, {
          ...delta,
          status: delta.status === "acked" ? "pending" : delta.status,
        });
      }

      if (restoredCount === 0) {
        return {};
      }

      return {
        pendingDeltas: Array.from(pendingById.values()).sort(
          (a, b) => a.nextRevision - b.nextRevision,
        ),
        lastSource: "persisted_mutation_log",
        lastSourceReason: "restorePendingDeltas",
      };
    });
    return restoredCount;
  },

  getSnapshot: () => {
    const state = get();
    return {
      viewRData: state.viewRData,
      revision: state.revision,
      hash: state.lastHash,
      authority: state.authority,
      currentSpaceIndex: state.currentSpaceIndex,
      spacesCount: state.spacesCount,
    };
  },

  reset: () => {
    set({ ...initialState, pendingDeltas: [] });
  },
}));

let legacyBridgeRefCount = 0;
let legacyBridgeCleanup: (() => void) | null = null;

export function startViewRDataLegacyEventBridge(): () => void {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  legacyBridgeRefCount += 1;

  if (!legacyBridgeCleanup) {
    const handleSidebarViewRData = (event: Event): void => {
      if (window.__viewr_homeNavigationInProgress === true) {
        return;
      }
      const detail = (event as CustomEvent<ViewRDataLegacyEventDetail>).detail;
      useViewRDataStore.getState().ingestLegacyEvent(detail, {
        source: "sidebar:setViewRData",
        sourceReason: detail?.sourceReason,
        transactionId: detail?.transactionId,
      });
    };

    window.addEventListener("sidebar:setViewRData", handleSidebarViewRData);
    legacyBridgeCleanup = () => {
      window.removeEventListener(
        "sidebar:setViewRData",
        handleSidebarViewRData,
      );
    };
  }

  return () => {
    legacyBridgeRefCount = Math.max(0, legacyBridgeRefCount - 1);
    if (legacyBridgeRefCount === 0 && legacyBridgeCleanup) {
      legacyBridgeCleanup();
      legacyBridgeCleanup = null;
    }
  };
}

export const viewRDataSelectors = {
  snapshot: (state: ViewRDataStoreState): ViewRDataSnapshot => ({
    viewRData: state.viewRData,
    revision: state.revision,
    hash: state.lastHash,
    authority: state.authority,
    currentSpaceIndex: state.currentSpaceIndex,
    spacesCount: state.spacesCount,
  }),
  pendingDeltas: (state: ViewRDataStoreState): ViewRDataDelta[] =>
    state.pendingDeltas,
  currentViewRData: (state: ViewRDataStoreState): ViewRData | null =>
    state.viewRData,
};
